import React, { useState, useEffect } from 'react';
import { MapPin, Briefcase, Clock, ArrowRight, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/jobs`);
        const data = await response.json();
        setJobs(data);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleApply = (job) => {
    setSelectedJob(job);
    setError('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('job_id', selectedJob.id.toString());
      formDataToSend.append('job_title', selectedJob.title);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      if (formData.coverLetter) {
        formDataToSend.append('cover_letter', formData.coverLetter);
      }

      const response = await axios.post(`${BACKEND_URL}/api/careers/apply`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Application submitted:', response.data);
      setAppliedJobs([...appliedJobs, selectedJob.id]);
      setSelectedJob(null);
      setFormData({ name: '', email: '', phone: '', coverLetter: '' });
      alert('Application submitted successfully! We will review your application and get back to you soon.');
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Failed to submit application. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="careers" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">Loading job openings...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="careers" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-primary font-semibold text-sm tracking-wider uppercase">Careers</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Join Our Team
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Be part of a dynamic team that's shaping the future of construction. We offer exciting opportunities for growth and development.
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-6">
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No job openings available at the moment. Please check back later!</p>
            </div>
          ) : (
            jobs.map((job) => (
            <Card 
              key={job.id}
              className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <h3 className="text-2xl font-bold text-gray-900">
                        {job.title}
                      </h3>
                      <Badge className="bg-primary text-white">
                        {job.type}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mb-4 text-gray-600">
                      <div className="flex items-center gap-2">
                        <Briefcase size={18} className="text-primary" />
                        <span className="text-sm">{job.department}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin size={18} className="text-primary" />
                        <span className="text-sm">{job.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={18} className="text-primary" />
                        <span className="text-sm">{job.experience}</span>
                      </div>
                    </div>

                    <p className="text-gray-600 leading-relaxed">
                      {job.description}
                    </p>
                  </div>

                  <div className="md:ml-6">
                    <Button
                      onClick={() => handleApply(job)}
                      disabled={appliedJobs.includes(job.id)}
                      className={appliedJobs.includes(job.id)
                        ? "bg-green-600 hover:bg-green-600 text-white cursor-default"
                        : "bg-primary hover:bg-primary/90 text-white"
                      }
                    >
                      {appliedJobs.includes(job.id) ? 'Applied' : 'Apply Now'}
                      {!appliedJobs.includes(job.id) && <ArrowRight className="ml-2" size={18} />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-white p-12 rounded-lg shadow-lg max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Don't see the right position?
          </h3>
          <p className="text-gray-600 text-lg mb-6">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <Button 
            className="bg-primary hover:bg-primary/90 text-white text-lg px-8 h-12"
            onClick={() => alert('Resume submission form would open here. This is a demo.')}
          >
            Submit Your Resume
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>

      {/* Application Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Apply for {selectedJob?.title}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitApplication} className="space-y-6 mt-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {error}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={submitLoading}
                  placeholder="John Doe"
                  className="border-gray-300 focus:border-primary focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={submitLoading}
                  placeholder="john@example.com"
                  className="border-gray-300 focus:border-primary focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                disabled={submitLoading}
                placeholder="+91 98765 43210"
                className="border-gray-300 focus:border-primary focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Letter (Optional)
              </label>
              <Textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                disabled={submitLoading}
                rows={5}
                placeholder="Tell us why you're interested in this position..."
                className="border-gray-300 focus:border-primary focus:ring-primary resize-none"
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> After submitting this form, please email your resume to{' '}
                <a href="mailto:careers@florentinainfra.com" className="text-primary hover:underline">
                  careers@florentinainfra.com
                </a>{' '}
                with the subject line "{selectedJob?.title} Application"
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSelectedJob(null)}
                disabled={submitLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitLoading}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {submitLoading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Careers;
